import React from 'react';
import { AlertCircle, Coins, ShoppingBag, X } from 'lucide-react';
import TokenPurchase from './TokenPurchase';
import { useLanguage } from '../contexts/LanguageContext';

interface TokenLimitModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  onPurchaseComplete: () => void;
}

const TokenLimitModal: React.FC<TokenLimitModalProps> = ({ 
  isOpen, 
  onClose, 
  userId, 
  onPurchaseComplete 
}) => {
  const { t } = useLanguage();

  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" aria-hidden="true"></div>
        
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
        
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="absolute top-0 right-0 pt-4 pr-4">
            <button
              onClick={onClose}
              className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
            >
              <span className="sr-only">{t('common.close')}</span>
              <X className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
          
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                <AlertCircle className="h-6 w-6 text-red-600" aria-hidden="true" />
              </div>
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                  {t('tokens.limitModal.title')}
                </h3>
                <div className="mt-2">
                  <p className="text-sm text-gray-500">
                    {t('tokens.limitModal.message')}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="mt-5 sm:mt-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center text-sm mb-3">
                  <Coins className="h-4 w-4 text-amber-500 mr-2" />
                  <span className="font-medium text-gray-700">
                    {t('tokens.limitModal.whatToDo')}
                  </span>
                </div>
                
                <p className="text-xs text-gray-600 mb-3">
                  {t('tokens.limitModal.buyTokens')}
                </p>
                
                <div className="mt-4">
                  <TokenPurchase 
                    userId={userId} 
                    onPurchaseComplete={(success) => {
                      if (success) {
                        onPurchaseComplete();
                        onClose();
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={onClose}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-gray-100 text-base font-medium text-gray-700 hover:bg-gray-200 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm"
            >
              {t('tokens.limitModal.close')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TokenLimitModal;